import { useCallback, useEffect, useState } from "react";
import { Target, Loader2, CheckCircle, AlertTriangle, RefreshCw, Settings2, XCircle } from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";

/**
 * Seguimiento de conversiones por servidor (superadmin): registros reales que
 * llegan desde un anuncio → Google Ads (registro verificado, primera reserva,
 * pago). Muestra el estado y permite configurarlo y sincronizar.
 */

interface ConversionStatus {
  enabled: boolean;
  dataManager: { configured: boolean; serviceAccountEmail: string | null };
  actionsError: string | null;
  actions: Array<{ type: "signup" | "activated" | "paid"; name: string; configured: boolean; primaryForGoal: boolean }>;
  otherPrimarySignupActions: string[];
  last30Days: { attributedSignups: number; withClickId: number; resolvedToKeyword: number };
  events: Record<string, number>;
  recentErrors: Array<{ type: string; error: string; at: string }>;
  trackingSince: string | null;
}

const TYPE_LABEL: Record<string, string> = {
  signup: "Registro verificado",
  activated: "Primera reserva",
  paid: "Suscripción de pago",
};

export default function GoogleAdsConversionsCard() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const [status, setStatus] = useState<ConversionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"setup" | "sync" | null>(null);
  const [connectionTest, setConnectionTest] = useState<{ ok: boolean; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ success: true; data: ConversionStatus }>(
        "/admin/google-ads/conversions/status",
      );
      setStatus(data.data);
      setError(null);
    } catch (err) {
      setError(extractApiErrorMessage(err, "No se pudo cargar el seguimiento de conversiones"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSetup = useCallback(async () => {
    const others = status?.otherPrimarySignupActions ?? [];
    const ok = await confirm({
      title: "¿Configurar el seguimiento de conversiones?",
      message: `Crea en Google Ads las conversiones que falten ("Registro verificado" como principal, "Primera reserva" y "Suscripción de pago")${
        others.length ? `, deja "${others.join('", "')}" como secundaria para no contar doble` : ""
      } y prueba la conexión de subida sin registrar nada. No afecta al gasto.`,
      confirmText: "Configurar",
    });
    if (!ok) return;
    setBusy("setup");
    try {
      const { data } = await apiClient.post<{
        success: true;
        message: string;
        data: { connectionTest: { ok: boolean; message: string } };
      }>("/admin/google-ads/conversions/setup");
      setConnectionTest(data.data.connectionTest);
      showToast(data.message, "success");
      await load();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo configurar"), "error");
    } finally {
      setBusy(null);
    }
  }, [confirm, load, showToast, status?.otherPrimarySignupActions]);

  const handleSync = useCallback(async () => {
    setBusy("sync");
    try {
      const { data } = await apiClient.post<{ success: true; data: Record<string, number> }>(
        "/admin/google-ads/conversions/sync",
      );
      const r = data.data;
      showToast(
        `Sincronizado: ${r.detected ?? 0} nuevas, ${r.sent ?? 0} enviadas, ${r.uploaded ?? 0} confirmadas, ${r.failed ?? 0} con error`,
        "success",
      );
      await load();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo sincronizar"), "error");
    } finally {
      setBusy(null);
    }
  }, [load, showToast]);

  const allConfigured = status?.actions.every((a) => a.configured) ?? false;
  const fullyActive = allConfigured && (status?.dataManager.configured ?? false);
  const count = (type: string, state: string) => status?.events[`${type}.${state}`] ?? 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-600" /> Seguimiento de conversiones
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Registros reales que llegan desde un anuncio (con cookies de marketing aceptadas), subidos a Google desde el
            servidor.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => void handleSync()}
            disabled={busy !== null || !fullyActive}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sincronizar ahora
          </button>
          {status && (
            <button
              onClick={() => void handleSetup()}
              disabled={busy !== null || loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {busy === "setup" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings2 className="w-4 h-4" />}
              {allConfigured ? "Comprobar" : "Configurar"}
            </button>
          )}
        </div>
      </div>

      {loading && !status ? (
        <p className="flex items-center gap-2 text-sm text-gray-500 mt-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </p>
      ) : error ? (
        <p className="flex items-center gap-2 text-sm text-red-600 mt-4">
          <AlertTriangle className="w-4 h-4" /> {error}
        </p>
      ) : status ? (
        <div className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            {status.actions.map((action) => (
              <div key={action.type} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                  {action.configured ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  {TYPE_LABEL[action.type]}
                  {action.primaryForGoal && (
                    <span className="ml-auto text-[10px] uppercase tracking-wide text-primary-700 font-semibold">Principal</span>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1.5">
                  {action.configured
                    ? `${count(action.type, "uploaded")} confirmadas · ${count(action.type, "sent") + count(action.type, "pending")} en curso · ${count(action.type, "failed")} fallidas`
                    : status.actionsError
                      ? "No se pudo consultar Google"
                      : "Sin configurar"}
                </p>
              </div>
            ))}
          </div>

          {status.actionsError && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2 break-words">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              No se pudo consultar Google Ads ahora mismo ({status.actionsError}). Pulsa "Comprobar" o vuelve a intentarlo en un
              momento.
            </p>
          )}

          <div
            className={`rounded-lg border px-3 py-2.5 text-sm ${
              status.dataManager.configured ? "border-emerald-100 bg-emerald-50 text-emerald-800" : "border-amber-100 bg-amber-50 text-amber-800"
            }`}
          >
            {status.dataManager.configured ? (
              <p className="flex items-start gap-2 break-words">
                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                Subida conectada con la cuenta de servicio {status.dataManager.serviceAccountEmail}.
              </p>
            ) : (
              <div>
                <p className="font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Falta conectar la subida de conversiones (Data Manager API)
                </p>
                <ol className="list-decimal pl-5 mt-2 space-y-1 text-amber-900">
                  <li>Google Cloud, proyecto reserva-manager: activa la "Data Manager API".</li>
                  <li>IAM → Cuentas de servicio: crea una cuenta y descarga una clave JSON.</li>
                  <li>Google Ads → Administrador → Acceso y seguridad: añade el email de esa cuenta con acceso Estándar.</li>
                  <li>
                    Railway: variable <code className="bg-white/60 px-1 rounded">GOOGLE_DATAMANAGER_SERVICE_ACCOUNT_JSON</code> con el
                    contenido del JSON. Después pulsa "Comprobar".
                  </li>
                </ol>
              </div>
            )}
          </div>

          {connectionTest && (
            <p
              className={`text-sm flex items-start gap-2 break-words ${connectionTest.ok ? "text-emerald-700" : "text-red-600"}`}
            >
              {connectionTest.ok ? (
                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              )}
              Prueba de conexión: {connectionTest.message}
            </p>
          )}

          <p className="text-sm text-gray-600">
            Últimos 30 días: <strong>{status.last30Days.attributedSignups}</strong> registros con origen de campaña,{" "}
            <strong>{status.last30Days.withClickId}</strong> desde un anuncio de Google y{" "}
            <strong>{status.last30Days.resolvedToKeyword}</strong> con su campaña y palabra clave identificadas.
          </p>

          {status.otherPrimarySignupActions.length > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              "{status.otherPrimarySignupActions.join('", "')}" sigue como conversión principal: al configurar pasa a
              secundaria para no contar doble.
            </p>
          )}

          {!status.enabled && (
            <p className="text-sm text-amber-700">La subida automática está desactivada en el servidor.</p>
          )}

          {status.recentErrors.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Últimos errores</p>
              <ul className="mt-1 space-y-1">
                {status.recentErrors.map((item, index) => (
                  <li key={index} className="text-xs text-gray-600 break-words">
                    <strong>{TYPE_LABEL[item.type] ?? item.type}:</strong> {item.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
